<template>
  <div class="modal-wrapper">
    <div class="mb-4" data-testid="last-heartbeat" v-if="lastHeartbeat > 0">
      <span class="subtitle1">{{
        $t(`modals.${ServiceAction.CONNECTION_STATUS}.lastHeartbeat`)
      }}</span>
      <div class="mt-2" :class="{ 'text-yellow-base': isHeartbeatIssue }">
        {{ getFormattedDateTime(lastHeartbeat) }}
      </div>
    </div>
    <div v-if="config.item.name === Service.OFFICE_365">
      <div class="subtitle1">{{ $t("cloudApplications.auditLogEnabled") }}</div>
      <div
        class="mt-4 mb-4"
        :class="{
          'text-red-dark': !config.item.auditLogEnabled,
          'text-green-dark': config.item.auditLogEnabled,
        }"
      >
        {{ config.item.auditLogEnabled ? $t("general.enabled") : $t("general.disabled") }}
      </div>
      <div v-if="!config.item.auditLogEnabled">
        <div class="mb-4" v-html="$t('cloudApplications.auditLogDisabledDescription')"></div>
        <a class="coro-link" :href="CONNECTING_TO_CORO_LINK" target="_blank">
          <v-icon icon="$activityLogsXs" size="18"></v-icon>
          {{ $t("cloudApplications.howToEnableAuditLog") }}
        </a>
        <v-alert icon="icon-warning-xs" color="indigo-pale" class="text-primary mt-4">
          <div v-html="$t('cloudApplications.enableAuditLogNotice')"></div>
        </v-alert>
      </div>
    </div>
    <v-divider v-if="enablePermissionsSection"></v-divider>
    <div class="mt-4" v-if="enablePermissionsSection">
      <div class="d-flex justify-space-between align-center">
        <span class="subtitle1">{{ $t("cloudApplications.permissions") }}</span>
        <v-btn
          v-if="showGrantPermissionsButton"
          variant="outlined"
          rounded
          color="primary"
          @click="grantPermissions()"
        >
          {{ $t("cloudApplications.grantPermissions") }}
        </v-btn>
      </div>
      <div class="mt-4">
        <div
          v-for="permission of config.item.permissions"
          :key="permission.scope"
          class="d-flex align-center mb-2"
        >
          <v-icon
            :class="{ 'text-red-dark': !permission.granted }"
            :icon="permission.granted ? '$check' : '$x'"
          ></v-icon
          >{{ permission.scope }}
        </div>
      </div>
    </div>
    <div class="d-flex justify-end mt-10">
      <v-btn
        rounded
        color="primary"
        data-testid="google-signup-cancel-btn"
        @click="$emit('dismissed')"
      >
        {{ $t("general.cancel") }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { CloudAppsRedirectUrl, Service, ServiceAction } from "@/constants/cloud-apps";
import { useI18n } from "vue-i18n";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useDialogsStore } from "@/_store/dialogs.module";
import moment from "moment";
import type { ConnectAppModalConfigItem } from "@/views/settings/cloud-apps/CloudAppsPage.vue";
import { componentDialogsConfigConstructor, getFormattedDateTime } from "@/_helpers/utils";
import { ModalWidth } from "@/constants/modals";
import GsuiteGrantPrivileges from "@/views/settings/cloud-apps/modals/GsuiteGrantPrivileges.vue";
import { useRoute } from "vue-router";
import { RouteName } from "@/constants/routes.ts";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<ConnectAppModalConfigItem>>,
      required: true,
    },
  },
  emits: ["change-header-title", "dismissed", "hide-default-footer"],
  setup(props, { emit }) {
    const CONNECTING_TO_CORO_LINK =
      "https://docs.coro.net/protection/connecting-to-m365/#connecting-to-coro:~:text=In%20the%20Microsoft%20Purview%20portal%2C%20select%20Start%20recording%20user%20and%20admin%20activity%20to%20enable%20audit%20log%20recording%3A";
    const i18n = useI18n();
    const dialogsModule = useDialogsStore();
    const cloudAppsModule = useCloudAppsStore();
    const route = useRoute();

    watch(
      () => props.config.item.name,
      (val) => {
        emit(
          "change-header-title",
          i18n.t(`modals.${ServiceAction.CONNECTION_STATUS}.title`, {
            service: i18n.t(`services.${val}`),
          })
        );
      },
      {
        immediate: true,
      }
    );
    const lastHeartbeat = ref(0);

    const isHeartbeatIssue = computed(() => {
      return moment().diff(moment(lastHeartbeat.value), "hours") > 1;
    });

    const enablePermissionsSection = computed(() => {
      return (
        props.config.item.permissions &&
        [Service.OFFICE_365, Service.G_SUITE].includes(props.config.item.name)
      );
    });

    const showGrantPermissionsButton = computed(() => {
      return props.config.item.permissions?.some((permission) => !permission.granted);
    });

    const openConnectServiceModal = (service: Service) => {
      const item = {
        name: service,
        serviceStatus: "active",
        signup: false,
      };

      const config = componentDialogsConfigConstructor({
        width: ModalWidth.LARGE,
        action: ServiceAction.GRANT_PRIVILEGES,
        item,
        component: GsuiteGrantPrivileges,
        disable: false,
        callback: () => {},
        hideFooter: true,
      });

      dialogsModule.openDialog(config);
    };

    const grantPermissions = async () => {
      const service = props.config.item.name;

      if (service === Service.G_SUITE) {
        emit("dismissed");
        openConnectServiceModal(Service.G_SUITE);
      } else {
        await cloudAppsModule.connect({
          redirectLocation:
            route.name === RouteName.DASHBOARD_PAGE
              ? CloudAppsRedirectUrl.DASHBOARD
              : CloudAppsRedirectUrl.CLOUD_APPS_SETTINGS,
          service,
          grantPermissions: true,
        });
      }
    };

    onMounted(async () => {
      lastHeartbeat.value = await cloudAppsModule.getLastHeartbeat(props.config.item.name);
    });

    return {
      grantPermissions,
      enablePermissionsSection,
      isHeartbeatIssue,
      ServiceAction,
      Service,
      showGrantPermissionsButton,
      lastHeartbeat,
      CONNECTING_TO_CORO_LINK,
      getFormattedDateTime,
    };
  },
});
</script>
<style scoped lang="scss">
:deep(*) {
  .text-red-dark::before {
    color: rgb(var(--v-theme-red-dark));
  }
}

:deep(.text-primary) {
  .v-icon:before {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.v-alert {
  padding: 4px 16px !important;
}

.coro-link {
  text-decoration: none !important;

  .v-icon:before {
    color: rgb(var(--v-theme-link)) !important;
  }
}

.modal-wrapper {
  max-height: calc(100vh - 240px);
  overflow-y: auto;
}
</style>
